import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Sidebar } from './Dashboard';
import { API } from '../App';
import { describeBillingError, isBillingError } from '../billingMessages';

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#0f0f1a',
    display: 'flex',
  },
  main: {
    flex: 1,
    padding: '40px',
    overflowY: 'auto',
  },
  pageTitle: {
    color: '#ffffff',
    fontSize: '28px',
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  pageSubtitle: {
    color: '#a0a0b0',
    fontSize: '14px',
    marginBottom: '40px',
  },
  topBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  addButton: {
    padding: '12px 24px',
    backgroundColor: '#e94560',
    border: 'none',
    borderRadius: '10px',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  card: {
    backgroundColor: '#16213e',
    borderRadius: '16px',
    padding: '24px',
    marginBottom: '24px',
  },
  cardTitle: {
    color: '#ffffff',
    fontSize: '16px',
    fontWeight: 'bold',
    marginBottom: '20px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    marginBottom: '16px',
  },
  label: {
    color: '#a0a0b0',
    fontSize: '12px',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    marginBottom: '6px',
    display: 'block',
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    backgroundColor: '#0f3460',
    border: '1px solid #0f3460',
    borderRadius: '8px',
    color: '#ffffff',
    fontSize: '14px',
    boxSizing: 'border-box',
    outline: 'none',
  },
  saveButton: {
    padding: '12px 24px',
    backgroundColor: '#e94560',
    border: 'none',
    borderRadius: '8px',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
    marginRight: '12px',
  },
  cancelButton: {
    padding: '12px 24px',
    backgroundColor: 'transparent',
    border: '1px solid #0f3460',
    borderRadius: '8px',
    color: '#a0a0b0',
    fontSize: '14px',
    cursor: 'pointer',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    color: '#a0a0b0',
    fontSize: '12px',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    padding: '12px 16px',
    textAlign: 'left',
    borderBottom: '1px solid #0f3460',
  },
  td: {
    color: '#ffffff',
    fontSize: '14px',
    padding: '14px 16px',
    borderBottom: '1px solid #0f3460',
  },
  badge: {
    padding: '4px 10px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: 'bold',
  },
  badgeActive: {
    backgroundColor: '#0a3a0a',
    color: '#00cc00',
  },
  badgeInactive: {
    backgroundColor: '#3a0a0a',
    color: '#e94560',
  },
  actionButton: {
    padding: '6px 14px',
    backgroundColor: 'transparent',
    border: '1px solid #0f3460',
    borderRadius: '6px',
    color: '#a0a0b0',
    fontSize: '12px',
    cursor: 'pointer',
    marginRight: '8px',
  },
  csvButton: {
    padding: '12px 24px',
    backgroundColor: '#0f3460',
    border: '1px solid #0f3460',
    borderRadius: '10px',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
    marginLeft: '12px',
  },
  success: {
    color: '#00cc00',
    fontSize: '14px',
    marginBottom: '16px',
  },
  error: {
    color: '#e94560',
    fontSize: '14px',
    marginBottom: '16px',
  },
  searchInput: {
    padding: '10px 14px',
    backgroundColor: '#0f3460',
    border: '1px solid #0f3460',
    borderRadius: '8px',
    color: '#ffffff',
    fontSize: '14px',
    outline: 'none',
    width: '250px',
  },
};

function Dealers() {
  const isAdmin = localStorage.getItem('admin_role') === 'admin';
  const token = localStorage.getItem('admin_token');
  const [dealers, setDealers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState('');
  const [newDealer, setNewDealer] = useState({ payroll_number: '', first_name: '', last_name: '', shift: 'Day' });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileRef = React.useRef();

   useEffect(() => {
    loadDealers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadDealers() {
    const res = await fetch(`${API}/admin/dealers`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const data = await res.json();
    if (Array.isArray(data)) setDealers(data);
  }

  async function addDealer() {
    if (!newDealer.payroll_number || !newDealer.first_name || !newDealer.last_name) {
      setError('All fields are required');
      return;
    }
    setLoading(true);
    setError('');
    setShowUpgrade(false);
    try {
      const res = await fetch(`${API}/admin/dealers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(newDealer),
      });
      if (res.ok) {
        setMessage('Dealer added successfully');
        setNewDealer({ payroll_number: '', first_name: '', last_name: '' });
        setShowAdd(false);
        loadDealers();
      } else {
        const data = await res.json();
        setError(describeBillingError(data.detail, 'Failed to add dealer'));
        setShowUpgrade(isBillingError(data.detail));
      }
    } catch (e) {
      setError('Could not connect to server');
    }
    setLoading(false);
  }

  async function toggleDealer(id, active) {
    await fetch(`${API}/admin/dealers/${id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ active: !active }),
    });
    loadDealers();
  }

  async function updateShift(id, shift) {
    await fetch(`${API}/admin/dealers/${id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ shift }),
    });
    loadDealers();
  }
  
  async function uploadCSV(e) {
    const file = e.target.files[0];
    if (!file) return;
    const text = await file.text();
    const lines = text.split('\n').filter(l => l.trim());
    const dealers = lines.slice(1).map(line => {
      const parts = line.split(',');
      const shift = parts[3]?.trim();
      return {
        payroll_number: parts[0]?.trim(),
        first_name: parts[1]?.trim(),
        last_name: parts[2]?.trim(),
        shift: ['Day', 'Swing', 'Grave'].includes(shift) ? shift : 'Day',
      };
    }).filter(d => d.payroll_number && d.first_name && d.last_name);

    try {
      const res = await fetch(`${API}/admin/dealers/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ dealers }),
      });
      const data = await res.json();
      if (res.ok) {
        setMessage(`Uploaded ${data.created} dealers successfully`);
        setShowUpgrade(false);
        loadDealers();
      } else {
        setError(describeBillingError(data.detail, 'Upload failed'));
        setShowUpgrade(isBillingError(data.detail));
      }
    } catch (e) {
      setError('Upload failed');
    }
  }

  const filtered = dealers.filter(d =>
    d.last_name.toLowerCase().includes(search.toLowerCase()) ||
    d.first_name.toLowerCase().includes(search.toLowerCase()) ||
    d.payroll_number.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={styles.container}>
      <Sidebar active="Dealers" />
      <div style={styles.main}>
        <div style={styles.pageTitle}>Dealers</div>
        <div style={styles.pageSubtitle}>Manage your dealer roster</div>

        {message && <div style={styles.success}>{message}</div>}
        {error && (
          <div style={styles.error}>
            {error}
            {showUpgrade && (
              <Link to="/billing" style={{ marginLeft: '12px', color: '#e94560', fontWeight: 'bold' }}>
                Go to Billing →
              </Link>
            )}
          </div>
        )}

        <div style={styles.topBar}>
          <input
            style={styles.searchInput}
            placeholder="Search by name or payroll #"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {isAdmin && (
            <div>
              <button style={styles.addButton} onClick={() => setShowAdd(!showAdd)}>
                + Add Dealer
              </button>
              <button style={styles.csvButton} onClick={() => fileRef.current.click()}>
                Upload CSV
              </button>
              <input
                ref={fileRef}
                type="file"
                accept=".csv"
                style={{ display: 'none' }}
                onChange={uploadCSV}
              />
            </div>
          )}
        </div>

        {showAdd && (
          <div style={styles.card}>
            <div style={styles.cardTitle}>Add New Dealer</div>
            <div style={styles.grid}>
              <div>
                <label style={styles.label}>Payroll Number</label>
                <input
                  style={styles.input}
                  value={newDealer.payroll_number}
                  onChange={e => setNewDealer({ ...newDealer, payroll_number: e.target.value })}
                  placeholder="e.g. EMP001"
                />
              </div>
              <div>
                <label style={styles.label}>First Name</label>
                <input
                  style={styles.input}
                  value={newDealer.first_name}
                  onChange={e => setNewDealer({ ...newDealer, first_name: e.target.value })}
                  placeholder="First name"
                />
              </div>
              <div>
                <label style={styles.label}>Last Name</label>
                <input
                  style={styles.input}
                  value={newDealer.last_name}
                  onChange={e => setNewDealer({ ...newDealer, last_name: e.target.value })}
                  placeholder="Last name"
                />
              </div>
              <div>
                <label style={styles.label}>Shift</label>
                <select
                  style={styles.input}
                  value={newDealer.shift}
                  onChange={e => setNewDealer({ ...newDealer, shift: e.target.value })}
                >
                  <option value="Day">Day</option>
                  <option value="Swing">Swing</option>
                  <option value="Grave">Grave</option>
                </select>
              </div>
            </div>
            <button style={styles.saveButton} onClick={addDealer} disabled={loading}>
              {loading ? 'Saving...' : 'Save Dealer'}
            </button>
            <button style={styles.cancelButton} onClick={() => setShowAdd(false)}>
              Cancel
            </button>
          </div>
        )}

        <div style={styles.card}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Payroll #</th>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Shift</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(dealer => (
                <tr key={dealer.id}>
                  <td style={styles.td}>{dealer.payroll_number}</td>
                  <td style={styles.td}>{dealer.first_name} {dealer.last_name}</td>
                  <td style={styles.td}>
                    {isAdmin ? (
                      <select
                        style={{ ...styles.actionButton, cursor: 'pointer' }}
                        value={dealer.shift || 'Day'}
                        onChange={e => updateShift(dealer.id, e.target.value)}
                      >
                        <option value="Day">Day</option>
                        <option value="Swing">Swing</option>
                        <option value="Grave">Grave</option>
                      </select>
                    ) : (
                      dealer.shift || 'Day'
                    )}
                  </td>
                  <td style={styles.td}>
                    <span style={{
                      ...styles.badge,
                      ...(dealer.active ? styles.badgeActive : styles.badgeInactive)
                    }}>
                      {dealer.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={styles.td}>
                    {isAdmin && (
                      <button
                        style={styles.actionButton}
                        onClick={() => toggleDealer(dealer.id, dealer.active)}
                      >
                        {dealer.active ? 'Deactivate' : 'Activate'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td style={{ ...styles.td, color: '#a0a0b0' }} colSpan="4">
                    No dealers found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default Dealers;